import React, { ReactNode } from "react";
import styled from "styled-components";
import { LucideIcon } from "lucide-react";
import { STAT_STRIP_ID } from "testIds";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import { NEW_LOOK_PHONE_MAX_WIDTH } from "common/NewLook/layout.ts";

const VALUE_MIN_FONT_SIZE = 12;

export interface StatStripItem {
  key: string;
  /** Kept for callers; the strip no longer draws decorative icon tiles. */
  icon?: LucideIcon;
  label: ReactNode;
  value: ReactNode;
  /** Small line under the value, e.g. a change or context. */
  meta?: ReactNode;
  /** Flat variant: a slightly larger figure, for the one that leads. */
  emphasis?: boolean;
}

interface StatStripProps {
  items: StatStripItem[];
  className?: string;
  /**
   * "connected" (default): one bordered panel with hairlines between figures.
   * "flat": no container; figures sit on the surface, sized to their content.
   * Used by the detail drawers.
   */
  variant?: "connected" | "flat";
}

/**
 * A row of key figures in one panel, separated by hairlines: label, figure,
 * then a quiet meta line. No icon tiles: the label says what the figure is.
 */
const StatStrip = ({ items, className, variant }: StatStripProps) =>
  variant === "flat" ? (
    <FlatWrapper className={className} data-testid={STAT_STRIP_ID}>
      {items.map((item) => (
        <FlatItem key={item.key}>
          <FlatLabel>{item.label}</FlatLabel>
          <FlatValue $emphasis={item.emphasis}>{item.value}</FlatValue>
          {item.meta && <FlatMeta>{item.meta}</FlatMeta>}
        </FlatItem>
      ))}
    </FlatWrapper>
  ) : (
    <Wrapper
      className={className}
      $count={items.length}
      data-testid={STAT_STRIP_ID}
    >
      {items.map((item) => (
        <Item key={item.key}>
          <Label>{item.label}</Label>
          <Value>
            <AutoSizeText
              inheritFont={true}
              minFontSizePx={VALUE_MIN_FONT_SIZE}
            >
              {item.value}
            </AutoSizeText>
          </Value>
          {item.meta && <Meta>{item.meta}</Meta>}
        </Item>
      ))}
    </Wrapper>
  );

export default StatStrip;

// Flat: no container, no dividers; each figure is as wide as its content.
const FlatWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  column-gap: 36px;
  row-gap: 16px;
  width: 100%;
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 20px;
  }
`;
const FlatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const FlatLabel = styled.div`
  font-size: 12.5px;
  line-height: 18px;
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
`;
const FlatValue = styled.div<{ $emphasis?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: ${(p) => (p.$emphasis ? 24 : 20)}px;
  line-height: ${(p) => (p.$emphasis ? 30 : 26)}px;
  font-weight: 500;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  * {
    font-size: inherit;
    font-weight: inherit;
  }
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    font-size: ${(p) => (p.$emphasis ? 22 : 18)}px;
  }
`;
const FlatMeta = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;

// Four figures fold into 2 × 2 (not 3 + 1) on narrower screens.
const getNarrowColumns = (count: number) =>
  count === 4 ? 2 : Math.min(count, 3);

const Wrapper = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
  overflow: hidden;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${(p) => p.theme.radii.card}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
  @media (max-width: 1100px) {
    grid-template-columns: repeat(
      ${(p) => getNarrowColumns(p.$count)},
      minmax(0, 1fr)
    );
  }
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    grid-template-columns: repeat(
      ${(p) => Math.min(p.$count, 2)},
      minmax(0, 1fr)
    );
  }
`;
// Hairlines on each item's left and top edge; the panel clips the outer ones,
// so the dividers stay right however the items wrap.
const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 20px;
  min-width: 0;
  box-shadow: -1px 0 0 ${(p) => getNewLookTokens(p.theme).hairline},
    0 -1px 0 ${(p) => getNewLookTokens(p.theme).hairline};
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    padding: 12px 14px;
  }
`;
const Label = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    white-space: normal;
    font-size: 11.5px;
  }
`;
const Value = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    font-size: 17px;
    line-height: 22px;
  }
`;
const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
